import { useEffect, useMemo, useState } from "react";
// User message — right-aligned dark-grey pill. Mirrors Reputifly AI Local
// .msg--user .msg__text (App.css:438-516).
//
// Double-click (desktop) / double-tap (mobile) the text bubble copies the
// message to clipboard. iOS fires dblclick when two taps land within ~500ms;
// touch-action: manipulation on .msg--user .msg__text removes the 300ms
// click-delay + suppresses double-tap-to-zoom so the gesture is snappy.
//
// Image persistence: the optimistic `blobUrl` is browser-local (lost on
// reload). When it's gone but we still have a `serverPath`, fall back to
// fetching the file via the authed GET /api/uploads/:subdir/:filename
// endpoint. The Attachment subcomponent wraps the hook so we can call it
// per-attachment without breaking the rules-of-hooks.
//
// Reload back-compat: messages persisted before today have `files=[paths]`
// but no `userAttachments`. Derive a synthetic userAttachments[] from
// `files` so historical images can render via the new GET endpoint.

import type { UIMessage } from "../lib/types";
import { useSession } from "../store/session";
import { DELIVERY_FAILED_REASON, QUEUE_LOST_REASON, TOO_LONG_REASON } from "../store/delivery";
import { visibleSendFailure } from "../lib/sendFailure";
import { formatReviewDraft, parseReviewDraft } from "../lib/reviewReferences";
import ReviewText from "./ReviewText";

import UserAttachments, { userAttachments } from "./UserAttachments";
import { UserMarkdown } from "./Markdown";
import { cachedSiteTags, linkTagsInMarkdown, onSiteTagsChange } from "../lib/siteTags";

// System notices arrive as user-role lines that start with an emoji ("⚙️ Auto-resume: …").
// Inter can't draw emoji (julian-ui), so the mark becomes a Lucide icon in front of the
// words (ported from Build, 9 Oct 2026).
const NOTICE_RE = /^(\u2699\uFE0F?|\u23F0\uFE0F?|\u{1F331})\s*/u;
function NoticeIcon({ mark, auto }: { mark: string; auto: boolean }) {
  const common = { className: "msg__noticeIcon", viewBox: "0 0 24 24", width: 16, height: 16, fill: "none", stroke: "currentColor",
    strokeWidth: 1.75, strokeLinecap: "round" as const, strokeLinejoin: "round" as const, "aria-hidden": true };
  // Lucide rotate-ccw (Auto-resume), clock (reminders), sprout, settings (anything else).
  if (auto) return <svg {...common} data-icon="rotate-ccw"><path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8" /><path d="M3 3v5h5" /></svg>;
  if (mark.startsWith("\u23F0")) return <svg {...common} data-icon="clock"><circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" /></svg>;
  if (mark.startsWith("\u{1F331}")) return <svg {...common} data-icon="sprout"><path d="M7 20h10" /><path d="M10 20c5.5-2.5.8-6.4 3-10" /><path d="M9.5 9.4c1.1.8 1.8 2.2 2.3 3.7-2 .4-3.5.4-4.8-.3-1.2-.6-2.3-1.9-3-4.2 2.8-.5 4.4 0 5.5.8z" /><path d="M14.1 6a7 7 0 0 0-1.1 4c1.9-.1 3.3-.6 4.3-1.4 1-1 1.6-2.3 1.7-4.6-2.7.1-4 1-4.9 2z" /></svg>;
  return <svg {...common} data-icon="settings"><path d="M12.22 2h-.44a2 2 0 0 0-2 2v.18a2 2 0 0 1-1 1.73l-.43.25a2 2 0 0 1-2 0l-.15-.08a2 2 0 0 0-2.73.73l-.22.38a2 2 0 0 0 .73 2.73l.15.1a2 2 0 0 1 1 1.72v.51a2 2 0 0 1-1 1.74l-.15.09a2 2 0 0 0-.73 2.73l.22.38a2 2 0 0 0 2.73.73l.15-.08a2 2 0 0 1 2 0l.43.25a2 2 0 0 1 1 1.73V20a2 2 0 0 0 2 2h.44a2 2 0 0 0 2-2v-.18a2 2 0 0 1 1-1.73l.43-.25a2 2 0 0 1 2 0l.15.08a2 2 0 0 0 2.73-.73l.22-.39a2 2 0 0 0-.73-2.73l-.15-.08a2 2 0 0 1-1-1.74v-.5a2 2 0 0 1 1-1.74l.15-.09a2 2 0 0 0 .73-2.73l-.22-.38a2 2 0 0 0-2.73-.73l-.15.08a2 2 0 0 1-2 0l-.43-.25a2 2 0 0 1-1-1.73V4a2 2 0 0 0-2-2z" /><circle cx="12" cy="12" r="3" /></svg>;
}

/** Your words: with Attention review references the reference chips render as before
 *  (ReviewText); otherwise as Markdown (bold, lists, code, links), raw HTML stays text. */
function UserWords({ text, references }: { text: string; references: Parameters<typeof ReviewText>[0]["references"] }) {
  if (references.length) return <ReviewText text={text} references={references} />;
  if (!text.trim()) return null;
  const match = text.match(NOTICE_RE);
  if (!match) return <TaggedWords text={text} />;
  const rest = text.slice(match[0].length);
  return <div className="msg__notice">
    <NoticeIcon mark={match[1]!} auto={/^Auto-resume/i.test(rest)} />
    <UserMarkdown text={rest} />
  </div>;
}

/** Your words with @site tags (16 Oct 2026): each known `@handle` is a link chip; tapping it
 *  opens that site's chat in place (the link also works if opened in a new tab). */
function TaggedWords({ text }: { text: string }) {
  const [tags, setTags] = useState(() => cachedSiteTags());
  useEffect(() => onSiteTagsChange(() => setTags(cachedSiteTags())), []);
  const md = useMemo(() => linkTagsInMarkdown(text, tags), [text, tags]);
  if (md === text) return <UserMarkdown text={text} />;
  return <div onClick={(e) => {
    const a = (e.target as Element).closest?.("a[href*='&tag=1']") as HTMLAnchorElement | null;
    if (!a || e.metaKey || e.ctrlKey || e.shiftKey) return;
    const id = new URL(a.href).searchParams.get("chat");
    if (!id) return;
    e.preventDefault();
    void useSession.getState().selectCompany(id);
  }}><UserMarkdown text={md} className="md--tagged" /></div>;
}

interface Props {
  message: UIMessage;
}

export default function UserBubble({ message }: Props) {
  const reviewDraft = parseReviewDraft(message.content ?? "");
  const references = message.reviewReferences ?? reviewDraft.references;
  const setToast = useSession((s) => s.setToast);
  const timeText = formatMessageTime(message.createdAt);
  // Is this user message still in the local queue waiting on a previous
  // turn to finish? When yes, render dimmed + "queued" badge so it's
  // visually obvious which message claude is currently working on.
  // Match by `userMsgId` (set on the QueuedMessage at creation time) —
  // matching by text would misfire on duplicate content ("ok" twice in a
  // row would light up BOTH bubbles as queued). Cheap O(n) over a tiny
  // queue (≤5 items in practice).
  //
  // Two states surface in the chip text:
  //   - dispatching=false → "⏳ queued"     (claude still on a previous turn)
  //   - dispatching=true  → "⏳ sending…"   (backend drained from queue,
  //                                          waiting for claude's first event)
  // The chip vanishes when claude actually emits its first content for
  // the new placeholder. See session.ts applyStreamEvent dequeue logic.
  const activeCompanyId = useSession((s) => s.activeCompanyId);
  const queueByCompany = useSession((s) => s.queueByCompany);
  const queueEntry =
    message.role === "user" && activeCompanyId
      ? (queueByCompany[activeCompanyId] ?? []).find(
          (q) => q.userMsgId === message.id,
        )
      : undefined;
  const isQueued = !!queueEntry;
  const isDispatching = queueEntry?.dispatching === true;

  // Failed-send marker (P2-#4): persistent red border + tap-to-retry hint
  // after the 4s toast fades. Set in sendMessage's catch branches when
  // either the POST itself fails (sendInbound 502) or the queue-POST fails.
  // NOT set for mid-stream SSE errors — pollAllStatuses' inline
  // "⚠️ Session ended" reconciler covers that case.
  const failedUserMsgIds = useSession((s) => s.failedUserMsgIds);
  const setComposerDraft = useSession((s) => s.setComposerDraft);
  const clearFailedMarker = useSession((s) => s.clearFailedMarker);
  const failure = message.role === "user" ? failedUserMsgIds[message.id] : undefined;
  const isFailed = !!failure;
  // Sent, but the server hasn't confirmed it yet (network drop / backgrounded
  // phone). Neutral, not red: it is being checked and resent with the same id
  // only if the server does not have it.
  const unconfirmed = useSession((s) => message.role === "user" ? s.unconfirmedUserMsgIds?.[message.id] : undefined);
  const resendMessage = useSession((s) => s.resendMessage);
  const isUnconfirmed = !!unconfirmed && !isFailed;
  const retryResends = failure?.retry === "resend";

  function handleFailedClick() {
    if (!isFailed) return;
    if (retryResends && activeCompanyId) {
      // Delivery failure: resend the SAME message id (the server dedupes, so
      // this can never reach the agent twice).
      void resendMessage(activeCompanyId, message.id, { manual: true });
      return;
    }
    // Pull the original text back into the composer so the user can edit-
    // and-retry in one tap. Clearing the marker means the bubble loses its
    // red border immediately — if the retry also fails, the NEW user
    // bubble gets the marker (different UIMessage.id). The original failed
    // bubble stays in the chat as a record of what happened.
    setComposerDraft(formatReviewDraft(reviewDraft.text, references));
    clearFailedMarker(message.id);
    setToast("Failed message restored to composer — edit and re-send.", "info");
  }

  // Prefer the freshly-sent shape (with blobUrls); otherwise derive from
  // persisted `files[]` so historical messages still show their thumbnails.
  const attachments = userAttachments(message);

  function copyOnDoubleClick() {
    const text = reviewDraft.text.trim();
    if (!text) return;
    navigator.clipboard
      .writeText(text)
      .then(() => setToast("Copied message", "success"))
      .catch(() => setToast("Couldn't copy — clipboard blocked", "error"));
  }

  return (
    <div data-message-id={message.id} data-native-input-id={message.meta?.nativeInput?.state === "absorbed" ? message.id : undefined} className={`msg msg--user ${isQueued ? "msg--user--queued" : ""} ${isFailed ? "msg--user--failed" : ""} ${isUnconfirmed ? "msg--user--unconfirmed" : ""}`}>
      <UserAttachments attachments={attachments} />
      {(reviewDraft.text.trim() || isQueued) && <div
        className="msg__text"
        onClick={toggleTimeOnTap}
        onDoubleClick={(e) => { cancelTimeTap(e.currentTarget); void copyOnDoubleClick(); }}
        // Tapping the message itself never re-sends it (30 Sep 2026: a tap on a
        // long failed message, meant to read it, re-ran an already-answered
        // request). Retry lives only on the explicit hint button below.
        title={
          isFailed
            ? retryResends
              ? `${failure?.reason ?? "Not delivered"}`
              : `Send failed: ${failure?.reason ?? "unknown"}`
            : isUnconfirmed
              ? "Sent — waiting for the server to confirm it"
            : isDispatching
              ? "Sending now — claude has just finished the previous reply"
              : isQueued
                ? "Queued — will send when current reply finishes"
                : "Tap for time · double-click to copy"
        }
      >
        <UserWords text={reviewDraft.text} references={references} />
        {isQueued && (
          <span
            className={`msg__queuedBadge ${isDispatching ? "msg__queuedBadge--sending" : ""}`}
            aria-label={isDispatching ? "Sending" : "Queued"}
          >
            {isDispatching ? "⏳ sending…" : "⏳ queued"}
          </span>
        )}
      </div>}
      {message.meta?.nativeInput?.state === "absorbed" && <div className="msg__inputNote">Added during task</div>}
      {message.meta?.nativeInput?.state === "uncertain" && <div className="msg__inputNote">Delivery not confirmed · Not automatically resent</div>}
      {message.meta?.nativeInput?.state === "failed" && <div className="msg__inputNote">Not sent · Earlier task input</div>}
      {isFailed && (
        <button type="button" className="msg__failedHint" aria-live="polite" onClick={handleFailedClick}>
          {retryResends
            ? failure?.reason === QUEUE_LOST_REASON ? "Not delivered — tap to resend"
              : failure?.reason === DELIVERY_FAILED_REASON ? "Not delivered — tap to retry"
              : "Not sent — tap to retry"
            : failure?.reason === TOO_LONG_REASON ? "Not sent — too long to keep after a reload · tap to copy it back"
            : visibleSendFailure(failure?.reason) ?? "⚠ Send failed · tap to retry"}
        </button>
      )}
      {timeText && <div className="msg__time">
        <div className="msg__timeInner"><time dateTime={message.createdAt}>{timeText}</time></div>
      </div>}
      {isUnconfirmed && (
        <div className="msg__unconfirmedHint" role="status" aria-live="polite">
          Not confirmed yet · checking…
        </div>
      )}
    </div>
  );
}

// Message date/time in Singapore time, e.g. "April 20, 4:20 AM" (year added when not this year).
const SGT = "Asia/Singapore";
function formatMessageTime(iso: string | undefined | null): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const year = (x: Date) => new Intl.DateTimeFormat("en-US", { timeZone: SGT, year: "numeric" }).format(x);
  const opts: Intl.DateTimeFormatOptions = { timeZone: SGT, month: "long", day: "numeric", hour: "numeric", minute: "2-digit", hour12: true };
  if (year(d) !== year(new Date())) opts.year = "numeric";
  // "October 9, 7:06 PM" (Intl would say "October 9 at 7:06 PM").
  const parts = new Intl.DateTimeFormat("en-US", opts).formatToParts(d);
  const get = (t: string) => parts.find((x) => x.type === t)?.value ?? "";
  const date = `${get("month")} ${get("day")}${opts.year ? `, ${get("year")}` : ""}`;
  return `${date}, ${get("hour")}:${get("minute")} ${get("dayPeriod")}`;
}

// One tap shows the time under the bubble, the next tap hides it; a double tap copies.
// The single tap waits a moment so a double tap never also toggles the time. Plain DOM
// state (data-open) so a re-render never closes it and the bubble stays hook-free.
const tapTimers = new WeakMap<Element, number>();
function cancelTimeTap(el: Element) {
  const t = tapTimers.get(el);
  if (t) { window.clearTimeout(t); tapTimers.delete(el); }
}
function toggleTimeOnTap(e: { detail: number; target: EventTarget; currentTarget: HTMLElement }) {
  const bubble = e.currentTarget;
  const time = bubble.parentElement?.querySelector<HTMLElement>(":scope > .msg__time");
  if (!time || e.detail > 1) return;
  if ((e.target as HTMLElement).closest("a, button")) return;
  if (window.getSelection()?.toString()) return;
  cancelTimeTap(bubble);
  tapTimers.set(bubble, window.setTimeout(() => {
    tapTimers.delete(bubble);
    time.dataset.open = time.dataset.open === "true" ? "false" : "true";
  }, 260));
}
